'use client';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/Icon';
import { ZipCityButton } from '@/components/ZipCity';
import { useShellState } from '@/lib/useShellState';

interface FooterLocationSearchProps {
  /**
   * The phone pair the `(main)` layout resolved for the pathname it
   * server-rendered. The layout does not re-run on a client-side navigation,
   * so from the first one onwards the pair comes from `useShellState` — the
   * same round trip the Header and the Sticky CTA Bar read.
   */
  phone?: string;
  matelsoPhoneLabel?: string;
}

export const FooterLocationSearch = ({
  phone: initialPhone,
  matelsoPhoneLabel: initialMatelsoPhoneLabel,
}: FooterLocationSearchProps) => {
  const t = useTranslations('locationSearch');
  const pathname = usePathname();
  const { phone, matelsoPhoneLabel } = useShellState(
    { phone: initialPhone, matelsoPhoneLabel: initialMatelsoPhoneLabel },
    (state) => ({
      phone: state.phone,
      matelsoPhoneLabel: state.matelsoPhoneLabel,
    }),
  );

  if (pathname.startsWith('/nachhilfe/')) {
    return null;
  }

  return (
    <>
      <div className="w-full lg:w-2/5 lg:shrink-0 lg:pr-12">
        <div className="flex flex-col">
          <p className="h3 mb-6">{t('footerTitle')}</p>
          <div className="mb-6">
            <ZipCityButton
              placeholder={t('placeholder')}
              buttonLabel={t('footerCta')}
            />
          </div>
          {phone && (
            <div className="text-center">
              <p className="mb-2 text-sm">{t('phoneLabel')}</p>
              <a
                href={`tel:${phone.replace(/[\s/]/g, '')}`}
                className="text-primary flex items-center justify-center gap-2 font-bold"
              >
                <Icon src="/icons/svg/phone-filled.svg" size={16} />
                {matelsoPhoneLabel || phone}
              </a>
            </div>
          )}
        </div>
      </div>
      <div className="mx-6 hidden w-px shrink-0 bg-[#28190a] lg:block" />
    </>
  );
};
